import { useEffect, useMemo, useState } from 'react';
import {
  applyResolvedTheme,
  getStoredThemeMode,
  getSystemPrefersDark,
  resolveTheme,
  setStoredThemeMode,
  type ThemeMode,
} from '@/lib/theme';

export function useThemeMode() {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => getStoredThemeMode());
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => getSystemPrefersDark());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches);
    };

    setSystemPrefersDark(media.matches);
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }

    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  const resolvedTheme = useMemo(
    () => resolveTheme(themeMode, systemPrefersDark),
    [systemPrefersDark, themeMode],
  );

  useEffect(() => {
    setStoredThemeMode(themeMode);
  }, [themeMode]);

  useEffect(() => {
    applyResolvedTheme(resolvedTheme);
  }, [resolvedTheme]);

  return {
    themeMode,
    resolvedTheme,
    setThemeMode,
  };
}
